import { rem } from 'polished'
import { useCallback, useEffect } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import Carousel from 'LuxKit/Carousel/Carousel'
import FilterChip from 'LuxKit/Chips/FilterChip'
import LineCheckIcon from 'LuxKit/Icons/line/LineCheckIcon'
import LineCoffeeIcon from 'LuxKit/Icons/line/LineCoffeeIcon'
import LineCrownIcon from 'LuxKit/Icons/line/LineCrownIcon'
import LineShieldCheckIcon from 'LuxKit/Icons/line/LineShieldCheckIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { trackClientEvent } from 'analytics/analytics'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppSelector } from 'hooks/reduxHooks'
import useUserHasLoyaltyBenefit from 'loyalty/hooks/useUserHasLoyaltyBenefit'
import getIsCurrentRegionUserLoyaltyRegion from 'loyalty/selectors/getIsCurrentRegionUserLoyaltyRegion'
import getIsLoyaltyEnabled from 'loyalty/selectors/getIsLoyaltyEnabled'
import getLoyaltyDisplayTier from 'loyalty/selectors/getLoyaltyDisplayTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import { getHasUpgradeableRate } from 'selectors/offerPage/offerPageSelectors'

const FilterBand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${rem(8)};
  padding: ${rem(12)} ${rem(16)};
  background-color: ${(props) => props.theme.palette.neutral.surface.lightBackground};

  ${mediaQueryUp.tablet} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(12)};
    padding: ${rem(12)} ${rem(20)};
    border-radius: ${(props) => props.theme.borderRadius.L};
  }
`

const ChipsCarousel = styled(Carousel)`
  align-self: stretch;
  min-width: 0;

  ${mediaQueryUp.tablet} {
    flex: 1 1 0;
    align-self: auto;
  }
`

interface Props {
  breakfastIncluded: boolean
  freeCancellation: boolean
  freeUpgrades: boolean
  hasUpgradeableRate: boolean
}

function toggleStateToClientEventAction(toggleState: boolean): 'toggled_on' | 'toggled_off' {
  return toggleState ? 'toggled_on' : 'toggled_off'
}

function PropertyQuickFilters({ breakfastIncluded, freeCancellation, freeUpgrades, hasUpgradeableRate }: Props) {
  const intl = useIntl()
  const offerPageDispatch = useOfferPageDispatch()

  const isLoyaltyEnabled = useAppSelector(getIsLoyaltyEnabled)
  const hasHotelUpgradeBenefit = useUserHasLoyaltyBenefit('hotel_upgrade')
  const isCurrentRegionUserLoyaltyRegion = useAppSelector(getIsCurrentRegionUserLoyaltyRegion)
  const isLoyaltyPackageUpgradeFeatureEnabled = useAppSelector(
    (state) => !!state.optimizely.optimizelyFeatureFlags[OptimizelyFeatureFlags.loyaltySppPackageUpgrades],
  )
  const tier = useAppSelector(getLoyaltyDisplayTier)
  const showFreeUpgradesChip = !!(
    hasUpgradeableRate &&
    isLoyaltyEnabled &&
    hasHotelUpgradeBenefit &&
    isCurrentRegionUserLoyaltyRegion &&
    isLoyaltyPackageUpgradeFeatureEnabled
  )

  // A rejected loyalty package upgrade can remove the last matching rate and hide this chip. Clear
  // the active filter at the same time so an invisible control cannot keep the rate list empty.
  useEffect(() => {
    if (freeUpgrades && !showFreeUpgradesChip) {
      offerPageDispatch({
        type: OfferPageActions.setQuickFilters,
        quickFilters: { freeUpgrades: false },
      })
    }
  }, [freeUpgrades, showFreeUpgradesChip, offerPageDispatch])

  const showPackagesWithLabel = intl.formatMessage({
    id: 'mN_Mn43wvhDnTmjLzo0P',
    defaultMessage: 'Show packages with:',
    description: 'Leading label introducing the row of package quick-filter chips on the offer page',
  })
  const freeUpgradesWithProgramLabel = intl.formatMessage({
    id: 'jQ5vPGCT_5cPdaz67oB2',
    defaultMessage: 'Free upgrades with {program} {tier}',
    description: 'Tag on the offer-page rate option promoting the free loyalty package upgrade, naming the members program and tier',
  }, { program: config.loyalty.programDisplayName, tier: getLoyaltyTierDisplayName(tier) })
  // Infinite stands on its own, without the program name in front of it.
  const freeUpgradesTierOnlyLabel = intl.formatMessage({
    id: 'P7rhTYvM3Qv7igxp_Jyg',
    defaultMessage: 'Free upgrades with {tier}',
    description: 'Tag on the offer-page rate option promoting the free loyalty package upgrade, naming only the members tier',
  }, { tier: getLoyaltyTierDisplayName(tier) })
  const freeUpgradesLabel = tier === 'infinite' ? freeUpgradesTierOnlyLabel : freeUpgradesWithProgramLabel
  const breakfastIncludedLabel = intl.formatMessage({
    id: 'w2jEyEHf_MDWoniUCBnT',
    defaultMessage: 'Breakfast included',
    description: 'Label for togglable filter chip that restricts displayed hotel rates to those with breakfast included',
  })
  const freeCancellationLabel = intl.formatMessage({
    id: 'Cnk7NVK8VWdi1ubUWdxY',
    defaultMessage: 'Free cancellation',
    description: 'Label for togglable filter chip that restricts displayed hotel rates to those with free cancellation',
  })

  const toggleFreeUpgrades = useCallback(() => {
    const newToggleState = !freeUpgrades
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { freeUpgrades: newToggleState },
    })
    trackClientEvent({
      subject: 'free_upgrades_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [freeUpgrades, offerPageDispatch])
  const toggleBreakfastIncluded = useCallback(() => {
    const newToggleState = !breakfastIncluded
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { breakfastIncluded: newToggleState },
    })
    trackClientEvent({
      subject: 'breakfast_included_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [breakfastIncluded, offerPageDispatch])
  const toggleFreeCancellation = useCallback(() => {
    const newToggleState = !freeCancellation
    offerPageDispatch({
      type: OfferPageActions.setQuickFilters,
      quickFilters: { freeCancellation: newToggleState },
    })
    trackClientEvent({
      subject: 'free_cancellation_quick_filter',
      action: toggleStateToClientEventAction(newToggleState),
      category: 'offer_page',
      type: 'interaction',
    })
  }, [freeCancellation, offerPageDispatch])

  return <FilterBand>
    <BodyText variant="medium" weight="bold">
      {showPackagesWithLabel}
    </BodyText>
    <ChipsCarousel gutterStyle="overflow" gap={8}>
      {showFreeUpgradesChip && <FilterChip
        size="medium"
        selected={freeUpgrades}
        startIcon={freeUpgrades ? <LineCheckIcon /> : <LineCrownIcon />}
        onClick={toggleFreeUpgrades}
      >
        {freeUpgradesLabel}
      </FilterChip>}
      <FilterChip
        size="medium"
        selected={breakfastIncluded}
        startIcon={breakfastIncluded ? <LineCheckIcon /> : <LineCoffeeIcon />}
        onClick={toggleBreakfastIncluded}
      >
        {breakfastIncludedLabel}
      </FilterChip>
      <FilterChip
        size="medium"
        selected={freeCancellation}
        startIcon={freeCancellation ? <LineCheckIcon /> : <LineShieldCheckIcon />}
        onClick={toggleFreeCancellation}
      >
        {freeCancellationLabel}
      </FilterChip>
    </ChipsCarousel>
  </FilterBand>
}

type MappedProps = Props

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  breakfastIncluded: !!state.quickFilters?.breakfastIncluded,
  freeCancellation: !!state.quickFilters?.freeCancellation,
  freeUpgrades: !!state.quickFilters?.freeUpgrades,
  hasUpgradeableRate: getHasUpgradeableRate(state, appState),
}), PropertyQuickFilters)
